import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MenuItem } from 'primeng/api';

import { ControlValueAccessorModel, getProvider, getValidatorProvider } from '../../../Forms/models/control-value-accessor.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';

import { BrandCustomInfo } from '../../../../api/brandIdentity/models/brand-identity.api.model';

export type CustomInfoGroup = FormGroup & {
    typeOptions?: DropdownOptionModel[];
    isCustomType?: boolean;
    hasTitleError?: boolean;
    hasInfoError?: boolean;
};

export interface BrandInfoConfig {
    label?: string;
    placeholder?: string;
    typeLabel?: string;
    typePlaceholder?: string;
    titleLabel?: string;
    titlePlaceholder?: string;
    titleRequired?: boolean;
    titleErrorMessage?: string;
    infoLabel?: string;
    infoPlaceholder?: string;
    infoRequired?: boolean;
    infoErrorMessage?: string;
    required?: boolean;
    useTextArea?: boolean;
}

@Component({
    selector: 'agpr-brand-custom-information',
    templateUrl: './brand-custom-information.component.html',
    styleUrls: ['./brand-custom-information.component.scss'],
    providers: [
        getProvider(BrandCustomInformationComponent),
        getValidatorProvider(BrandCustomInformationComponent)
    ],
    standalone: false
})
export class BrandCustomInformationComponent extends ControlValueAccessorModel<BrandCustomInfo[]> implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly infoConfig: BrandInfoConfig = {
        label: 'Common.brandInformation.customInfo.label',
        placeholder: 'Common.brandInformation.customInfo.noCustomInfoMessage',
        titleLabel: 'Common.brandInformation.customInfo.title',
        titlePlaceholder: 'Common.brandInformation.customInfo.title.placeholder',
        titleRequired: true,
        titleErrorMessage: 'Common.brandInformation.customInfo.title.empty',
        infoLabel: 'Common.brandInformation.customInfo.info',
        infoPlaceholder: 'Common.brandInformation.customInfo.info.placeholder',
        infoRequired: true,
        infoErrorMessage: 'Common.brandInformation.customInfo.info.empty',
        useTextArea: false
    };

    @Input() public id: string = 'brandCustomInfomation';

    @Input() public maxLimit: number = 10;
    @Input() public disabled: boolean = false;
    @Input()
    set config(value: BrandInfoConfig) {
        this._config = {
            ...this.infoConfig,
            ...value
        };
    }
    get config(): BrandInfoConfig {
        return this._config;
    }

    @Input() actions: MenuItem[] = [];

    @ViewChildren('listItem') listItem: QueryList<any>;

    private subscriptions: Subscription[] = [];

    _config: BrandInfoConfig = this.infoConfig;
    form: FormGroup;

    constructor(
        private fb: FormBuilder
    ) {
        super();
        this.form = this.fb.group({customInfo: this.fb.array<FormGroup>([])});
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.valueChanges.subscribe(() => {
                this.emitValue();
                this.refreshComputedState();
            })
        );
    }

    get customInfo(): FormArray<CustomInfoGroup> {
        return this.form.get('customInfo') as FormArray<CustomInfoGroup>;
    }

    get customAction(): MenuItem | undefined {
        return this.actions.find(action => action.isCustomType);
    }

    get customActionKey(): unknown {
        return this.customAction?.key;
    }

    add() {
        if (this.disabled || this.customInfo.length >= this.maxLimit) {
            return;
        }
        this.customInfo.push(this.createControl());
        requestAnimationFrame(() => {
            this.listItem.last.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        });
    }

    remove(index: number) {
        this.customInfo.removeAt(index);
    }

    onTypeChange(group: CustomInfoGroup, key: unknown) {
        const titleControl = group.get('title');
        const action = this.actions.find(item => item.key === key);

        if (action && action !== this.customAction) {
            titleControl?.setValue(action.label);

            return;
        }

        if (this.actions.some(item => item !== this.customAction && item.label === titleControl?.value)) {
            titleControl?.setValue('');
        }
    }

    private refreshComputedState(): void {
        this.customInfo.controls.forEach(group => {
            const key = group.get('infoType')?.value;

            group.isCustomType = key != null && this.actions.length > 0 && key === this.customActionKey;
            group.hasTitleError = !!group.get('title')?.hasError('required');
            group.hasInfoError = !!group.get('info')?.hasError('required');
            group.typeOptions = this.actions.map(action => {
                const option = new DropdownOptionModel(action.label, action.key);
                option.disabled = this.isTypeDisabled(action, group);

                return option;
            });
        });
    }

    private isTypeDisabled(action: MenuItem, group: CustomInfoGroup): boolean {
        if (action.maxLimit == null) {
            return false;
        }
        const count = this.customInfo.controls.filter(
            control => control !== group && control.get('infoType')?.value === action.key
        ).length;

        return count >= action.maxLimit;
    }

    createControl(value?: BrandCustomInfo): CustomInfoGroup {
        const matchedAction = this.actions.length
            ? this.actions.find(action => action.label === value?.title?.trim()) ?? this.customAction
            : undefined;

        return this.fb.group({
            id: [value?.id ?? null],
            infoType: [matchedAction?.key ?? null],
            title: [value?.title?.trim() ?? '', this.config.titleRequired ? { updateOn: 'blur', validators: [Validators.required]} : null],
            info: [value?.info?.trim() ?? '', this.config.infoRequired ? { updateOn: 'blur', validators: [Validators.required]} : null]
        });
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.form.invalid ? { invalid: true } : null;
    }

    emitValue() {
        this.value = this.customInfo.value.map(({ infoType, ...rest }) => rest);
        this.onModelChange(this.value);
        this.onModelTouched();
    }

    override writeValue(value: BrandCustomInfo[]): void {
        this.value = value ?? [];
        this.customInfo.clear({ emitEvent: false });
        this.value.forEach(item => this.customInfo.push(this.createControl(item), { emitEvent: false }));
        this.refreshComputedState();
    }

    override setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
